import { useEffect, useMemo, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { supabase } from '../supabase';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { REALMS } from '@wot/shared';
import { renderShareCard, renderDailyShareCard, dailyShareText } from '../lib/shareCard';
import { shareCard } from '../lib/shareFlow';
import { NewBadges } from '../components/NewBadges';
import { dailyNumber } from '@wot/shared';
import { computeSessionRead, PaceBand } from '../lib/cognitiveEngine';
import { loadLocalAtlasEvents } from '../lib/atlasData';
import { MatchDeltaCard } from '../components/AtlasRank';
import { trackSnapshotViewed, trackPMCtaClicked } from '../utils/analytics';
import { getGuestName } from '../lib/guestName';
import { useAuth } from '../hooks/useAuth';
import { PmInterstitial } from '../components/PmInterstitial';
import { PmQuestion } from '../components/PmQuestion';
import { SharePreview } from '../components/SharePreview';
import { MissedReview } from '../components/MissedReview';
import { PM_AD, recordGameCompleted, shouldShowPmAd } from '../lib/pmAd';
import { requestReview } from '../lib/review';
import { ReminderOffer } from '../components/ReminderOffer';

// The last match whose snapshot impression was reported; module scope so a
// remount of the screen cannot re-fire it. See the effect below.
let trackedSnapshotMatch: string | null = null;

// ── SIGNAL tokens (same language as ConfigScreen) ────────────────
const VIOLET = '#3E7BFA';
const TEAL = '#14B8A6';
const CORAL = '#F43F5E';
// Share links point at the Play Store listing (the growth funnel is
// share -> install). The id is the package name, so this URL is fixed and
// goes live the moment the app is published.
// The web address, not the store: a link that plays in the browser is
// worth more than one that asks for an install first. The Daily link opens
// the same ten questions the sender just played.
const SHARE_URL = 'https://wot.aimtech.ai/?utm_source=share&utm_medium=score';
const DAILY_SHARE_URL = 'https://wot.aimtech.ai/?daily=1&utm_source=share&utm_medium=daily';

const mono = { fontFamily: 'var(--font-mono)' as const };

export function Results() {
  const setView = useGameStore((s) => s.setView);
  const scores = useGameStore((s) => s.scores);
  const playerId = useGameStore((s) => s.playerId);
  const nickname = useGameStore((s) => s.nickname) || getGuestName();
  const totalRounds = useGameStore((s) => s.totalRounds) || 5;
  const correctCount = useGameStore((s) => s.correctAnswers) || 0;
  const timeMs = useGameStore((s) => s.totalResponseTimeMs) || 0;
  const roomId = useGameStore((s) => s.roomId);
  const gameMode = useGameStore((s) => s.gameMode);
  const pmEnabled = useGameStore((s) => s.pmPromotionsEnabled);
  const resetGame = useGameStore((s) => s.resetGame);
  const matchMode = useGameStore((s) => s.matchMode);
  const answerLog = useGameStore((s) => s.answerLog);
  const reviewLog = useGameStore((s) => s.reviewLog);
  const badgeSync = useGameStore((s) => s.badgeSync);
  const streakBonusTotal = useGameStore((s) => s.streakBonusTotal);

  // The interstitial decision is made once, at mount, from the finished
  // game count and the day's memory; the film covers this screen and hands
  // it back on skip, completion or CTA. Design: lib/pmAd.ts.
  const [showAd, setShowAd] = useState<boolean>(() => {
    if (!roomId) return false;
    return shouldShowPmAd(pmEnabled, recordGameCompleted());
  });

  const [matchData, setMatchData] = useState<any[]>([]);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  // Web without a file-capable share sheet: show the card instead of hiding it.
  const [sharePreview, setSharePreview] = useState<{ url: string; text: string } | null>(null);

  useEffect(() => {
    if (roomId && gameMode !== 'solo') {
      supabase.from('match_players')
        .select(`id, total_score, users ( nickname )`)
        .eq('match_id', roomId)
        .then(({ data, error }) => {
          if (!error && data) {
            setMatchData(data.sort((a, b) => b.total_score - a.total_score));
          }
        });
    }
  }, [roomId, gameMode]);

  const myScore = playerId ? (scores[playerId] || 0) : 0;
  const isMultiplayer = gameMode !== 'solo';
  const isDailyMatch = matchMode === 'DAILY';
  // A guest who joined by invite can finish a 1v1 but cannot open the next
  // one: a rematch needs an account. The button says so instead of failing.
  const { user: authUser, loading: authLoading, signInWithGoogle } = useAuth();
  // Not "guest" while the session is still being read: that flashed the
  // sign-in label at a signed-in player for a moment.
  const guestRematch = isMultiplayer && !authLoading && !authUser;

  // The rating prompt, after the score has landed and the ad slot has had its
  // turn. Play decides whether to show anything; lib/review.ts decides whether
  // this result deserves the ask at all, and never asks twice. The delay is so
  // the sheet does not land on top of the score animation.
  useEffect(() => {
    if (!roomId || showAd) return;
    const t = setTimeout(() => {
      void requestReview({ mode: matchMode, correct: correctCount, total: totalRounds, isMultiplayer });
    }, 2500);
    return () => clearTimeout(t);
  }, [roomId, showAd, matchMode, correctCount, totalRounds, isMultiplayer]);

  const avgDelay = totalRounds > 0 ? (timeMs / totalRounds / 1000).toFixed(1) : '0.0';
  const accuracy = totalRounds > 0 ? correctCount / totalRounds : 0;

  // ── Session Read: real per-answer sequence, nothing fabricated ──
  const read = useMemo(() => computeSessionRead(answerLog), [answerLog]);
  const streak = read.bestStreak; // honest longest run (was correctCount-1 guesswork)

  // ── Outcome (no emoji, no em-dash, SIGNAL palette only) ──
  let outcomeText = 'COMPLETE';
  let outcomeColor = '#fff';

  if (isMultiplayer && matchData.length > 1) {
    const winner = matchData[0];
    if (winner.id === playerId) {
      outcomeText = 'VICTORY';
      outcomeColor = TEAL;
    } else if (winner.total_score === matchData[1]?.total_score) {
      outcomeText = 'DRAW';
      outcomeColor = '#A1A1AA';
    } else {
      outcomeText = 'DEFEAT';
      outcomeColor = CORAL;
    }
  } else if (matchMode === 'SURVIVAL') {
    // Depth is the score that travels; points are secondary in this mode.
    // No subtitle: the number is the statement, commentary cheapens it.
    outcomeText = `DEPTH ${totalRounds}`;
    outcomeColor = totalRounds >= 21 ? TEAL : totalRounds >= 13 ? VIOLET : CORAL;
  } else {
    // Verdict only. The subtitles that used to live here were filler
    // commentary on a number the player can already see.
    if (accuracy === 1) {
      outcomeText = 'FLAWLESS';
      outcomeColor = TEAL;
    } else if (accuracy >= 0.8) {
      outcomeText = 'BRILLIANT';
      outcomeColor = TEAL;
    } else if (accuracy >= 0.6) {
      outcomeText = 'SOLID';
      outcomeColor = VIOLET;
    } else if (accuracy >= 0.4) {
      outcomeText = 'ROUGH';
      outcomeColor = CORAL;
    } else {
      outcomeText = 'OUCH';
      outcomeColor = CORAL;
    }
  }

  // ── Match Read presentation (weather, not climate — no identity claims;
  //    archetypes now live in the accumulated Atlas, unlocked over games) ──
  // Bar fill for pace is decorative rank of the band, not a pseudo-score.
  const PACE_BAR: Record<PaceBand, number> = {
    Instant: 92, Swift: 74, Measured: 55, Deliberate: 36, Unhurried: 18,
  };

  let ctaText = 'DISCOVER YOUR FULL PROFILE';
  if (accuracy >= 0.8) ctaText = 'VALIDATE YOUR PROFILE';
  else if (accuracy < 0.4) ctaText = 'FIND YOUR REAL STRENGTHS';

  const pmUrl = 'https://psychematrix.io?utm_source=wot&utm_medium=results&utm_content=session_read';

  // Impression: once per MATCH, not per mount. The empty-deps version fired
  // once per mount, and this screen mounts several times per visit (StrictMode
  // double-invoke plus view/URL sync remounts): PostHog showed six
  // snapshot_viewed for one game, which would inflate the funnel's key step.
  // The guard lives at module scope so remounts cannot reset it.
  useEffect(() => {
    if (!roomId || trackedSnapshotMatch === roomId) return;
    trackedSnapshotMatch = roomId;
    trackSnapshotViewed('SESSION_READ', accuracy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  async function handleRematch() {
    if (guestRematch) { void signInWithGoogle(); return; }
    setRematchLoading(true);
    // A rematch replays the same recipe: mode and arena carry the whole
    // config now, the old count and difficulty knobs no longer exist.
    const savedNickname = useGameStore.getState().nickname;
    const savedRealm = useGameStore.getState().selectedRealm;
    const savedMode = useGameStore.getState().selectedMode;
    resetGame();
    useGameStore.getState().setNickname(savedNickname);
    useGameStore.getState().setSelectedRealm(savedRealm);
    useGameStore.getState().setSelectedMode(savedMode);

    if (isMultiplayer && gameMode === 'create_room') {
      // A room match comes back as a room: same mode and arena, a fresh
      // invite link on the waiting screen. The other player needs the new
      // link; quick match would have thrown them at a stranger in Rush.
      useGameStore.getState().setGameMode('create_room');
      await useGameStore.getState().createRoom();
    } else if (isMultiplayer) {
      useGameStore.getState().setGameMode('quick_match');
      await useGameStore.getState().quickMatch();
    } else if (isDailyMatch) {
      // The Daily is once a day; "again" means a normal solo run, so pick.
      useGameStore.getState().setGameMode('solo');
      useGameStore.getState().setView('config');
    } else {
      // Straight back in: same mode, same arena, no setup screen. The setup
      // is one tap away on SETUP below for whoever wants a change.
      useGameStore.getState().setGameMode('solo');
      await useGameStore.getState().startSoloMatch(savedRealm);
    }
    setRematchLoading(false);
  }

  function handleSetup() {
    const savedNickname = useGameStore.getState().nickname;
    const savedRealm = useGameStore.getState().selectedRealm;
    const savedMode = useGameStore.getState().selectedMode;
    resetGame();
    useGameStore.getState().setNickname(savedNickname);
    useGameStore.getState().setSelectedRealm(savedRealm);
    useGameStore.getState().setSelectedMode(savedMode);
    useGameStore.getState().setGameMode('solo');
    useGameStore.getState().setView('config');
  }

  function handleHome() {
    resetGame();
    setView('home');
  }

  async function handleShare() {
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch { /* ignore */ }

    const pct = totalRounds > 0 ? Math.round((correctCount / totalRounds) * 100) : 0;
    const selectedRealm = useGameStore.getState().selectedRealm || 'ALL';
    const isDaily = matchMode === 'DAILY';
    const daily = isDaily ? {
      number: dailyNumber(),
      dots: answerLog.map((e) => e.correct),
      correct: correctCount,
      total: totalRounds,
      avg: avgDelay,
      streak: (() => { try { return JSON.parse(localStorage.getItem('wot_daily_v1') || 'null')?.streak ?? 1; } catch { return 1; } })(),
    } : null;
    const shareUrl = isDaily ? DAILY_SHARE_URL : SHARE_URL;
    const text = daily ? dailyShareText(daily, shareUrl).replace(`\n${shareUrl}`, '') : [
      `World of Trivia: ${outcomeText}`,
      `${correctCount}/${totalRounds} correct (${pct}%), ${avgDelay}s avg, ${myScore.toLocaleString('en-US')} pts`,
      'Can you beat me?',
    ].join('\n');

    // The card, then lib/shareFlow: the image where the platform takes one,
    // text with the link where it does not, copy and preview on a desktop.
    let blob: Blob | null = null;
    try {
      blob = daily ? await renderDailyShareCard(daily) : await renderShareCard({
        outcomeText,
        outcomeColor,
        nickname,
        score: myScore,
        correct: correctCount,
        total: totalRounds,
        avg: avgDelay,
        streak,
        realm: selectedRealm,
        realmColor: (REALMS as any)[selectedRealm]?.color || VIOLET,
        // The mode is the difficulty descriptor now; the share card prints it
        // as the game's label.
        difficulty: (matchMode ?? 'CLIMB').toLowerCase(),
      });
    } catch { /* no card: the text still goes */ }

    const done = await shareCard({
      card: isDaily ? 'daily' : 'score',
      blob,
      fileName: 'wot-score.png',
      text,
      url: shareUrl,
      dialogTitle: 'Share your score',
      web: 'sheet',
      props: { mode: matchMode ?? null },
    });
    if (done.outcome === 'copied') {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
    // A desktop browser: the card on screen, URL on its own line so it stays clickable.
    if (done.previewUrl) setSharePreview({ url: done.previewUrl, text: `${text}\n${shareUrl}` });
  }

  const cardStyle = { background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 14 };

  return (
    <div className="page-results" style={{ padding: '16px 0' }}>
      {showAd && (PM_AD.mode === 'question'
        ? <PmQuestion onDone={() => setShowAd(false)} />
        : <PmInterstitial onDone={() => setShowAd(false)} />)}
      {sharePreview && (
        <SharePreview url={sharePreview.url} text={sharePreview.text} fileName="wot-share.png" onClose={() => setSharePreview(null)} />
      )}
      <div style={{
        width: '100%', maxWidth: 400, margin: '0 auto', padding: '0 16px',
        display: 'flex', flexDirection: 'column', gap: 12, flex: 1, justifyContent: 'center',
      }}>

        {/* ── Outcome ──
            One block with the score, not two: the verdict, its reason, and
            the number it earned are a single thought, and the air that used
            to sit between them said nothing. */}
        <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ ...mono, fontSize: '0.8rem', letterSpacing: '0.25em', color: '#c6c6ce' }}>
            {isMultiplayer ? 'MATCH COMPLETE'
              : matchMode === 'SURVIVAL' ? 'RUN COMPLETE'
              : isDailyMatch ? `THE DAILY #${dailyNumber()}`
              : 'GAME COMPLETE'}
          </span>
          <h1 style={{
            margin: 0, fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
            fontSize: '1.4rem', letterSpacing: '0.02em', lineHeight: 1.1, color: outcomeColor,
          }}>
            {outcomeText}
          </h1>
        </div>

        {/* ── Score ── */}
        {/* A centered flex row overflowed on both edges once the font scale
            grew (",384 PTS vs 6,23" on a Galaxy at 1.3x). Now a grid whose
            columns can shrink, score type bound to the viewport, names
            ellipsized. Solo keeps the single centered column. */}
        {(() => {
          const hasOpp = isMultiplayer && matchData.length > 1;
          const opp = hasOpp ? matchData.find(m => m.id !== playerId) : null;
          const nameStyle = {
            ...mono, fontSize: '0.8rem', letterSpacing: '0.2em', color: '#c6c6ce',
            maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
          };
          return (
            <div className="animate-in" style={{
              display: 'grid',
              gridTemplateColumns: hasOpp ? 'minmax(0, 1fr) auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
              alignItems: 'center', columnGap: 14, animationDelay: '0.05s', marginTop: -4,
              width: '100%', minWidth: 0,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, minWidth: 0 }}>
                <span style={nameStyle}>{nickname.toUpperCase()}</span>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
                  // Smaller than the old 3.4rem on purpose: the score is one number among
                  // several on this screen, not a billboard, and the headroom is what
                  // keeps it inside a 360px column at a large font scale.
                  fontSize: hasOpp ? 'clamp(1.7rem, 8vw, 2.5rem)' : 'clamp(2rem, 10vw, 2.8rem)',
                  letterSpacing: '-0.03em', color: '#fff', lineHeight: 1, whiteSpace: 'nowrap',
                }}>
                  {myScore.toLocaleString('en-US')}
                  <span style={{ fontSize: '0.9rem', color: '#A8A8BC', fontWeight: 700, marginLeft: 6, fontStyle: 'normal', letterSpacing: '0.1em' }}>PTS</span>
                </span>
                {/* How much of it the runs paid: the STREAK BONUS, as the server scored it. */}
                {streakBonusTotal > 0 && (
                  <span style={{ ...mono, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', color: '#F59E0B', marginTop: 4, whiteSpace: 'nowrap' }}>
                    INCL. +{streakBonusTotal.toLocaleString('en-US')} STREAK BONUS
                  </span>
                )}
              </div>
              {hasOpp && (
                <>
                  <span style={{ color: '#808080', fontSize: '0.9rem' }}>vs</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, opacity: 0.55, minWidth: 0 }}>
                    <span style={nameStyle}>{(opp?.users?.nickname || 'OPP').toUpperCase()}</span>
                    <span style={{ ...mono, fontSize: 'clamp(1.2rem, 5.5vw, 1.6rem)', fontWeight: 700, color: '#fff', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                      {(opp?.total_score || 0).toLocaleString('en-US')}
                    </span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Stats ── */}
        <div className="animate-in" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, animationDelay: '0.1s' }}>
          {[
            { val: `${correctCount}/${totalRounds}`, label: 'CORRECT', color: TEAL },
            { val: `${avgDelay}s`, label: 'AVG SPEED', color: VIOLET },
            { val: `${streak}`, label: 'STREAK', color: CORAL },
          ].map(s => (
            <div key={s.label} style={{
              // Fixed height and centered content: on phones with large font
              // scaling "AVG SPEED" wraps to two lines, and without a fixed
              // height that one tile grew taller and broke the row.
              ...cardStyle, minHeight: 84, padding: '10px 4px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3,
              background: `linear-gradient(160deg, ${s.color}1A 0%, transparent 60%), #182541`,
              borderTop: `3px solid ${s.color}`,
            }}>
              <span style={{ ...mono, fontSize: '1.45rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</span>
              <span style={{ fontSize: '0.78rem', letterSpacing: '0.04em', color: '#C4C4D2', fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── A badge this game earned, the rarest first. Arrives a moment
            after the screen (award_badges runs once the match is closed). ── */}
        <NewBadges sync={badgeSync} nickname={nickname} />

        {/* ── Match Read: this match only, real observed sequence ── */}
        {read.n > 0 && (
        <div className="animate-in" style={{ ...cardStyle, background: 'linear-gradient(140deg, rgba(62,123,250,0.10) 0%, transparent 55%), #182541', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, animationDelay: '0.12s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: '0.85rem', letterSpacing: '0.14em', color: '#d2d2d8', fontWeight: 700 }}>
              MATCH READ
            </span>
            <span style={{ ...mono, fontSize: '0.8rem', letterSpacing: '0.1em', color: '#bebec6' }}>
              {read.n} ANSWERS
            </span>
          </div>

          {/* The match as it happened: one dot per round */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {read.timeline.map((e, i) => (
              <span
                key={i}
                title={`Q${e.roundIndex}`}
                style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  background: e.correct ? TEAL : `${CORAL}22`,
                  border: e.correct ? 'none' : `2px solid ${CORAL}`,
                  boxShadow: e.correct ? `0 0 8px ${TEAL}66` : 'none',
                  opacity: e.timedOut ? 0.45 : 1,
                }}
              />
            ))}
          </div>

          {/* Observed metrics only. Unobservable ones are absent, not faked. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {read.pace && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.06em', color: '#B8B8CA', flex: '1 1 auto', minWidth: 0 }}>PACE</span>
                  <span style={{ ...mono, fontSize: '0.85rem', fontWeight: 700, color: VIOLET, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
                    {read.pace.band} · {(read.pace.medianMs / 1000).toFixed(1)}s
                  </span>
                </div>
                <div style={{ height: 7, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${PACE_BAR[read.pace.band]}%`, height: '100%', borderRadius: 2, background: `linear-gradient(90deg, ${VIOLET}88, ${VIOLET})` }} />
                </div>
              </div>
            )}

            {read.steadiness && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.06em', color: '#B8B8CA', flex: '1 1 auto', minWidth: 0 }}>STEADINESS</span>
                  <span style={{ ...mono, fontSize: '0.85rem', fontWeight: 700, color: CORAL, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
                    {read.steadiness.band}
                  </span>
                </div>
                <div style={{ height: 7, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${read.steadiness.score}%`, height: '100%', borderRadius: 2, background: `linear-gradient(90deg, ${CORAL}88, ${CORAL})` }} />
                </div>
              </div>
            )}

            {(read.sharpestRegion || read.resilience) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {read.sharpestRegion && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.06em', color: '#B8B8CA', flex: '1 1 auto', minWidth: 0 }}>SHARPEST</span>
                    <span style={{ ...mono, fontSize: '0.85rem', fontWeight: 700, color: TEAL, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
                      {read.sharpestRegion.name} · {read.sharpestRegion.correct}/{read.sharpestRegion.n}
                    </span>
                  </div>
                )}
                {read.resilience && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.06em', color: '#B8B8CA', flex: '1 1 auto', minWidth: 0 }}>RECOVERY</span>
                    <span style={{ ...mono, fontSize: '0.85rem', fontWeight: 700, color: '#dcdce0', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
                      {read.resilience.recovered} of {read.resilience.observed}
                    </span>
                  </div>
                )}
              </div>
            )}

            {!read.pace && !read.steadiness && (
              <span style={{ fontSize: '0.85rem', color: '#ccccd4', fontStyle: 'italic', lineHeight: 1.5 }}>
                Too few answers for a pattern read. Longer games sharpen it.
              </span>
            )}
          </div>

          {pmEnabled && (
            <p className="snapshot-bridge-text" style={{ margin: 0 }}>
              This reads one match. PsycheMatrix maps the person: <strong>10</strong> behavioral dimensions, <strong>4</strong> character types.
            </p>
          )}
          {pmEnabled && (
            <a
              href={pmUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="snapshot-cta"
              onClick={() => trackPMCtaClicked('results', 'session_read')}
            >
              {ctaText} →
            </a>
          )}
        </div>
        )}

        {/* ── What this match did to the ladder. Chess.com's most addictive
            moment: every match has a price or a prize. Computed from the
            device-local answer record, which always contains the match that
            just ended. ── */}
        {answerLog.length > 0 && (
          <div className="animate-in" style={{ animationDelay: '0.15s', display: 'flex', justifyContent: 'center' }}>
            <MatchDeltaCard events={loadLocalAtlasEvents()} lastN={answerLog.length} />
          </div>
        )}

        {/* ── The misses, with their answers. Closed by default: the score
            is the headline, the lesson is one tap below it. ── */}
        {reviewLog.length > 0 && (
          <div className="animate-in" style={{ animationDelay: '0.17s', display: 'flex', justifyContent: 'center' }}>
            <MissedReview entries={reviewLog} matchId={roomId} nickname={nickname} />
          </div>
        )}

        {/* ── Actions ── */}
        <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 6, animationDelay: '0.18s' }}>
          <button
            onClick={handleRematch}
            disabled={rematchLoading}
            style={{
              width: '100%', height: 58,
              background: `linear-gradient(180deg, ${VIOLET} 0%, ${VIOLET}D8 100%)`, border: 'none', borderRadius: 12,
              boxShadow: `0 6px 0 #1E4FC2, 0 10px 26px ${VIOLET}44`,
              color: '#0A1222', fontFamily: 'var(--font-heading)', fontWeight: 900,
              fontSize: '1rem', letterSpacing: '0.14em', cursor: 'pointer',
              opacity: rematchLoading ? 0.5 : 1, transition: 'all 0.15s ease',
            }}
          >
            {rematchLoading ? 'SEARCHING' : guestRematch ? 'SIGN IN TO REMATCH' : isMultiplayer ? 'REMATCH' : isDailyMatch ? 'PLAY MORE' : 'PLAY AGAIN'}
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleShare}
              style={{
                flex: 1, height: 52, ...cardStyle, borderRadius: 12,
                color: shareCopied ? TEAL : '#ECECF2', fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '0.95rem', letterSpacing: '0.12em', cursor: 'pointer', transition: 'all 0.15s ease',
                borderColor: shareCopied ? TEAL : 'rgba(255,255,255,0.2)',
              }}
            >
              {shareCopied ? 'COPIED' : 'SHARE'}
            </button>
            {!isMultiplayer && !isDailyMatch && (
            <button
              onClick={handleSetup}
              style={{
                flex: 1, height: 52, ...cardStyle, borderRadius: 12,
                color: '#ECECF2', fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '0.95rem', letterSpacing: '0.12em', cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              SETUP
            </button>
            )}
            <button
              onClick={handleHome}
              style={{
                flex: 1, height: 52, ...cardStyle, borderRadius: 12,
                color: '#ECECF2', fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '0.95rem', letterSpacing: '0.12em', cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              HOME
            </button>
          </div>
        </div>

        {/* ── The Daily reminder, offered until answered (Android). Under the
            actions so PLAY AGAIN stays the screen's one big button. ── */}
        <ReminderOffer />
      </div>
    </div>
  );
}
